import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { useActivateNurse, useClearEmployeeStatus } from "../../pages/Profile/v2/hooks";
import { formatEmployeeStatusReason } from "../../utils/employeeStatusReason";
import { formatPersonFullName, getNurseDepartmentStatus, isNurseOnLeaveOrSuspended } from "../../utils/formatters";
import { EffectiveEmployeeStatus, type Nurse } from "../../utils/models";
import { AlertActionBadge } from "../UIToolkit/Badges/AlertActionBadge";
import EmployeeStatusChip from "../UIToolkit/Badges/EmployeeStatusChip";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import EditEmployeeStatusDialog from "./EditEmployeeStatusDialog";
import { getReasonTitle } from "./statusDecisions/copy";
import { useOpenStatusDecisions } from "./statusDecisions/hooks";
import { StatusDecisionDialog, useTrackStatusDecisionOpened } from "./statusDecisions/StatusDecisionDialog";

type Props = {
	nurse: Nurse;
	departmentId: string;
	disabled?: boolean;
	/** Re-fetch the profile after a status change so the chip/labels reflect the new effective status. */
	onStatusChanged: () => void;
};

type ActiveDialog = "edit" | "return" | "reactivate" | "cancel_scheduled";

/**
 * Self-contained per-department employment-status control for a nurse profile: renders the
 * status chip plus the right action(s) for the nurse's current effective status, and owns the
 * dialogs (edit status, return-to-active, reactivate, cancel-scheduled-deactivation) using the
 * status hooks directly. Renders for both active and inactive nurses.
 */
export const EmployeeStatusControl: FC<Props> = ({ nurse, departmentId, disabled = false, onStatusChanged }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { refreshNursesData } = useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const { clearEmployeeStatus, isClearingEmployeeStatus } = useClearEmployeeStatus();
	const { activateNurse, isActivatingNurse } = useActivateNurse();
	const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
	const [isDecisionDialogOpen, setIsDecisionDialogOpen] = useState(false);
	const trackDecisionOpened = useTrackStatusDecisionOpened();

	const { data: openDecisions } = useOpenStatusDecisions(departmentId);
	const nurseDecision = useMemo(() => openDecisions?.find((decision) => decision.nurseId === nurse.id) ?? null, [openDecisions, nurse.id]);

	const handleDecisionBadgeClick = useCallback(() => {
		if (!nurseDecision) return;
		trackDecisionOpened(nurseDecision, departmentId);
		setIsDecisionDialogOpen(true);
	}, [nurseDecision, trackDecisionOpened, departmentId]);

	const { status: effectiveStatus, reason: statusReason, endDate: statusEndDate } = getNurseDepartmentStatus(nurse, departmentId);
	const isOnLeaveOrSuspended = isNurseOnLeaveOrSuspended(nurse, departmentId);
	const isInactive = effectiveStatus === EffectiveEmployeeStatus.Inactive;

	const scheduledDeactivationDate = useMemo(() => {
		const dateStr = nurse.departmentScheduledDeactivationDates?.[departmentId];
		return dateStr ? DateTime.fromISO(dateStr) : null;
	}, [nurse.departmentScheduledDeactivationDates, departmentId]);

	const nurseName = formatPersonFullName(nurse, lastNameFirst);
	// Analytics keeps the order-independent "First Last" form so events stay comparable across departments.
	const analyticsNurseName = formatPersonFullName(nurse, false);
	const isBusy = disabled || isClearingEmployeeStatus || isActivatingNurse;

	const closeDialog = useCallback(() => setActiveDialog(null), []);

	const handleReturnToActiveConfirm = useCallback(async () => {
		await clearEmployeeStatus({ departmentId, nurseId: nurse.id });
		onStatusChanged();
		showSnackbar(t("employee.status.cleared"), "success");
		closeDialog();
	}, [clearEmployeeStatus, departmentId, nurse.id, onStatusChanged, showSnackbar, t, closeDialog]);

	const handleReactivateConfirm = useCallback(async () => {
		track("profile_reactivate_confirm", { nurseId: nurse.id, nurseName: analyticsNurseName });
		await activateNurse({ departmentId, nurseId: nurse.id });
		await refreshNursesData(departmentId);
		onStatusChanged();
		showSnackbar(t("employee.status.reactivated"), "success");
		closeDialog();
	}, [track, activateNurse, departmentId, nurse.id, analyticsNurseName, refreshNursesData, onStatusChanged, showSnackbar, t, closeDialog]);

	const handleCancelScheduledConfirm = useCallback(async () => {
		track("profile_cancel_scheduled_deactivation_confirm", { nurseId: nurse.id, nurseName: analyticsNurseName });
		await activateNurse({ departmentId, nurseId: nurse.id });
		await refreshNursesData(departmentId);
		onStatusChanged();
		showSnackbar(t("employee.status.cancel.scheduled.done"), "success");
		closeDialog();
	}, [track, activateNurse, departmentId, nurse.id, analyticsNurseName, refreshNursesData, onStatusChanged, showSnackbar, t, closeDialog]);

	const handleEditSaved = useCallback(() => {
		onStatusChanged();
		showSnackbar(t("employee.status.saved"), "success");
	}, [onStatusChanged, showSnackbar, t]);

	return (
		<Box data-testid="profile-employee-status-control" sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
			<EmployeeStatusChip
				status={effectiveStatus}
				reason={formatEmployeeStatusReason(statusReason, t)}
				endDate={statusEndDate}
				dataTestId="profile-employee-status-chip"
			/>

			{nurseDecision && (
				<AlertActionBadge
					dataTestId="profile-status-decision-badge"
					tooltip={getReasonTitle(t, nurseDecision.reason)}
					ariaLabel={t("team.status.decision.badge.aria")}
					onClick={handleDecisionBadgeClick}
				/>
			)}

			{isInactive ? (
				<SystemButton
					data-testid="profile-reactivate-button"
					buttonVariant="primary"
					size="small"
					onClick={() => setActiveDialog("reactivate")}
					disabled={isBusy}
					sx={{ width: "fit-content", height: "fit-content" }}
				>
					{t("employee.status.reactivate.action")}
				</SystemButton>
			) : (
				<>
					<SystemButton
						data-testid="profile-edit-employee-status-button"
						buttonVariant="secondary"
						size="small"
						onClick={() => setActiveDialog("edit")}
						disabled={isBusy}
						sx={{ width: "fit-content", height: "fit-content" }}
					>
						{t("employee.status.edit.action")}
					</SystemButton>

					{isOnLeaveOrSuspended && (
						<SystemButton
							data-testid="profile-return-to-active-button"
							buttonVariant="borderless"
							size="small"
							onClick={() => setActiveDialog("return")}
							disabled={isBusy}
							sx={{ width: "fit-content", height: "fit-content" }}
						>
							{t("employee.status.return.to.active")}
						</SystemButton>
					)}

					{scheduledDeactivationDate && (
						<SystemButton
							data-testid="profile-cancel-scheduled-deactivation-button"
							buttonVariant="borderless"
							size="small"
							onClick={() => setActiveDialog("cancel_scheduled")}
							disabled={isBusy}
							sx={{ width: "fit-content", height: "fit-content" }}
						>
							{t("employee.status.cancel.scheduled.action")}
						</SystemButton>
					)}
				</>
			)}

			{activeDialog === "edit" && <EditEmployeeStatusDialog open nurse={nurse} departmentId={departmentId} onClose={closeDialog} onSaved={handleEditSaved} />}

			{activeDialog === "return" && (
				<SystemDialog
					open
					onClose={closeDialog}
					title={t("employee.status.return.to.active")}
					body={t("employee.status.return.confirm", { name: nurseName })}
					onConfirm={handleReturnToActiveConfirm}
					cancel={t("cancel")}
					confirm={t("employee.status.return.to.active")}
					confirmLoading={isClearingEmployeeStatus}
					dataTestId="return-to-active"
				/>
			)}

			{activeDialog === "reactivate" && (
				<SystemDialog
					open
					onClose={closeDialog}
					title={t("employee.status.reactivate.title", { name: nurseName })}
					body={t("employee.status.reactivate.confirm", { name: nurseName })}
					onConfirm={handleReactivateConfirm}
					cancel={t("cancel")}
					confirm={t("employee.status.reactivate.confirm.button")}
					confirmLoading={isActivatingNurse}
					dataTestId="reactivate-nurse"
				/>
			)}

			{activeDialog === "cancel_scheduled" && scheduledDeactivationDate && (
				<SystemDialog
					open
					onClose={closeDialog}
					title={t("employee.status.cancel.scheduled.title")}
					body={t("employee.status.cancel.scheduled.confirm", { name: nurseName, date: scheduledDeactivationDate.toFormat("MMM d, yyyy") })}
					onConfirm={handleCancelScheduledConfirm}
					cancel={t("cancel")}
					confirm={t("employee.status.cancel.scheduled.confirm.button")}
					confirmLoading={isActivatingNurse}
					dataTestId="cancel-scheduled-deactivation"
				/>
			)}

			{isDecisionDialogOpen && (
				<StatusDecisionDialog decision={nurseDecision} departmentId={departmentId} nurseName={nurseName} onClose={() => setIsDecisionDialogOpen(false)} />
			)}
		</Box>
	);
};

export default EmployeeStatusControl;
